import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface UserRole {
  church_id: string;
  church_name: string;
  role: string;
}

export const useUserRole = () => {
  const { user, session } = useAuth();
  
  const queryResult = useQuery({
    queryKey: ['user-roles', user?.id, !!session?.access_token],
    queryFn: async (): Promise<{ roles: UserRole[], isSuperAdmin: boolean }> => {
      const userId = user?.id;
      const hasSession = !!(session && session.access_token);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🚀 useUserRole - Starting database query...', { userId, hasSession });
      }
      
      if (!userId || !hasSession) {
        if (process.env.NODE_ENV === 'development') {
          console.log('❌ useUserRole - Missing user or session');
        }
        return { roles: [], isSuperAdmin: false };
      }

      try {
        // Get user church roles with timeout
        const queryPromise = supabase
          .from('user_church_roles')
          .select('church_id, role')
          .eq('user_id', userId);

        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Query timeout after 8 seconds')), 8000);
        });

        const { data: userRoles, error: rolesError } = await Promise.race([
          queryPromise,
          timeoutPromise
        ]);

        if (process.env.NODE_ENV === 'development') {
          console.log('📊 useUserRole - Query completed:', { 
            userRoles: userRoles?.length || 0, 
            error: rolesError?.message || 'none' 
          });
        }

        if (rolesError) {
          console.error('❌ Database error:', rolesError);
          return { roles: [], isSuperAdmin: false };
        }

        if (!userRoles || userRoles.length === 0) {
          if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ No roles found for user');
          }
          return { roles: [], isSuperAdmin: false };
        }

        // Get church names for valid church IDs
        const churchIds = userRoles.map((r: any) => r.church_id).filter(Boolean);
        let churchData: any[] = [];
        
        if (churchIds.length > 0) {
          try {
            if (process.env.NODE_ENV === 'development') {
              console.log('🏛️ Fetching church names for:', churchIds.length, 'churches');
            }
            const { data: churches, error: churchError } = await supabase
              .from('churches')
              .select('id, name')
              .in('id', churchIds);

            if (!churchError && churches) {
              churchData = churches;
            }
          } catch (error) {
            console.warn('⚠️ Church names query failed:', error);
          }
        }

        // Combine the data
        const combinedRoles = userRoles.map((role: any) => ({
          church_id: role.church_id,
          church_name: churchData.find(c => c.id === role.church_id)?.name || 'Unknown Church',
          role: role.role
        }));
        
        const isSuperAdminResult = combinedRoles.some(role => role.role === 'super_admin');
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ useUserRole - Final result:', { 
            rolesCount: combinedRoles.length, 
            isSuperAdmin: isSuperAdminResult 
          });
        }

        return { 
          roles: combinedRoles, 
          isSuperAdmin: isSuperAdminResult 
        };

      } catch (error: any) {
        console.error('💥 useUserRole - Fatal error:', error);
        return { roles: [], isSuperAdmin: false };
      }
    },
    enabled: !!user?.id && !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000,
  });

  const roles = queryResult.data?.roles || [];
  const isSuperAdmin = queryResult.data?.isSuperAdmin || false;
  const loading = queryResult.isLoading;

  // Ensure we always return a safe object, never undefined
  try {
    return { 
      roles: Array.isArray(roles) ? roles : [], 
      isSuperAdmin: typeof isSuperAdmin === 'boolean' ? isSuperAdmin : false, 
      loading: typeof loading === 'boolean' ? loading : true 
    };
  } catch (error) {
    console.error('❌ Error in useUserRole return:', error);
    return {
      roles: [],
      isSuperAdmin: false,
      loading: false
    };
  }
};